import { isKiosk, storeDomain } from '../config/constants';

// rel tokens that close reverse-tabnabbing on a target="_blank" link.
const SAFE_REL_TOKENS = ['noopener', 'noreferrer'];

// Scheme prefix of an absolute url. Anything that does not match is a relative
// reference and therefore always our own page.
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i;

/**
 * Canonical form of a host for comparison: case-insensitive, without the FQDN
 * root dot and without a single leading `www.`.
 *
 * Dropping `www.` is required, not cosmetic: the prod envs ship the prefixed
 * host (`www.rebud.com`, `www.hof.delivery`, `www.everscore.com`,
 * `www.bundles.delivery`) while a CMS editor writes `https://rebud.com/shop`.
 * Without it every own-domain link would be treated as external.
 *
 * Exported for tests only — callers use isExternalHref / openExternalLinksInNewTab.
 *
 * @param {string} host
 * @returns {string} '' when there is nothing usable
 */
export function normalizeHost(host) {
  if (typeof host !== 'string') return '';

  let normalized = host.trim().toLowerCase();
  if (!normalized) return '';

  // "rebud.com." and "rebud.com" are the same host.
  if (normalized.endsWith('.')) normalized = normalized.slice(0, -1);
  if (normalized.startsWith('www.')) normalized = normalized.slice(4);

  return normalized;
}

/**
 * Does this href point outside our own store?
 *
 * Pure: no DOM, no env, no window. The decision is made on the *raw* attribute
 * value, never on the parsed document's `a.href` property — the base URI of a
 * DOMParser document is environment dependent (jsdom resolves it against
 * `http://localhost/`), so `a.href` would classify differently in tests and in
 * the browser.
 *
 * Only http(s) and protocol-relative urls can ever be external:
 *  - `mailto:` / `tel:` / `sms:` and other handler schemes are left alone —
 *    target="_blank" on them opens a blank tab next to the OS handler in some
 *    browsers;
 *  - `#anchor` stays in place (blog post table-of-contents links);
 *  - `?query`, `/path`, `path`, `foo.html` are our own pages.
 *
 * Host rule mirrors the "own host" rule already used for structured data (see
 * DispensaryContainer.js, jsonLdHost): the configured store domain is the source
 * of truth, with the current host as the fallback.
 *
 * Subdomains are deliberately NOT treated as own: the match is strict equality
 * after normalization, so `blog.rebud.com` is external for a `www.rebud.com`
 * store. Suffix matching was rejected because the brands share staging domains
 * under one apex (`logickit.io`), where a suffix rule would silently mark other
 * brands' hosts as our own.
 *
 * @param {string} href raw value of the href attribute
 * @param {Array<string>} ownHosts hosts considered internal; empty = nothing is external
 * @returns {boolean}
 */
export function isExternalHref(href, ownHosts) {
  if (typeof href !== 'string') return false;

  const raw = href.trim();
  if (!raw) return false;

  // Fail-safe: with no known own host we cannot tell ours from theirs, and
  // marking our own links external is worse than doing nothing.
  if (!Array.isArray(ownHosts) || !ownHosts.length) return false;

  // In-page anchor or query-only reference — same document.
  if (raw.startsWith('#') || raw.startsWith('?')) return false;

  let absolute;
  if (raw.startsWith('//')) {
    // Protocol-relative: the scheme is inherited from the page and irrelevant
    // here, only the host matters, so any http scheme parses it correctly.
    absolute = `https:${raw}`;
  } else if (raw.startsWith('/')) {
    // Root-relative path.
    return false;
  } else {
    const scheme = SCHEME_RE.exec(raw);
    // No scheme at all: a relative path such as `shop/x` or `foo.html`.
    if (!scheme) return false;

    const protocol = scheme[1].toLowerCase();
    if (protocol !== 'http' && protocol !== 'https') return false;

    absolute = raw;
  }

  let linkHost;
  try {
    // hostname, not host: the port must not take part in the comparison. On the
    // dev server the page host is `localhost:3000`, so a `http://localhost:3000/shop`
    // link would not match otherwise.
    linkHost = normalizeHost(new URL(absolute).hostname);
  } catch (e) {
    // Garbage href (`ht tp://`, `://`, …) — leave the link untouched.
    return false;
  }

  if (!linkHost) return false;

  return !ownHosts.some((host) => normalizeHost(host) === linkHost);
}

/**
 * Merge the safe rel tokens into an existing rel value.
 *
 * Merging, never overwriting: an SEO editor puts `rel="nofollow"` /
 * `rel="sponsored ugc"` on CMS links and replacing the value would silently
 * break it. Dedupe is case-insensitive, existing tokens keep their order,
 * redundant whitespace is collapsed.
 *
 * @param {string|null} rel
 * @returns {string}
 */
const mergeRel = (rel) => {
  const tokens = (typeof rel === 'string' ? rel : '').split(/\s+/).filter(Boolean);
  const present = new Set(tokens.map((token) => token.toLowerCase()));

  SAFE_REL_TOKENS.forEach((token) => {
    if (present.has(token)) return;
    tokens.push(token);
    present.add(token);
  });

  return tokens.join(' ');
};

/**
 * Pure core: rewrite every external link in an HTML string so it opens in a new
 * tab. Knows nothing about kiosk mode, the store config or `window.location` —
 * the own hosts arrive as an argument, which is what makes this testable.
 *
 * Parsing is done with DOMParser, not a regex: CMS HTML comes out of a WYSIWYG
 * editor where attribute order, quote style and `>` inside an attribute value
 * are all unpredictable, and a regex breaks on each of them.
 *
 * The string is returned untouched unless at least one link actually changed.
 * `doc.body.innerHTML` normalizes markup (closes stray tags, rewrites quotes and
 * entities), so reserialization must only ever happen for content that really
 * carries an external link — same guard as buildTocFromHtml in BlogPostDetails.
 *
 * @param {string} html
 * @param {Array<string>} ownHosts
 * @returns {string} the input itself when there is nothing to do
 */
export function rewriteExternalLinks(html, ownHosts) {
  // Return the input, not '': callers feed us values straight out of the API
  // (including JSON.parse results, which can be a number, an object or null),
  // and identity keeps the current rendering bit for bit.
  if (typeof html !== 'string' || !html.trim()) return html;

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return html;

  // Most CMS fields are plain paragraphs — they must not go through
  // reserialization at all.
  if (!/<a/i.test(html)) return html;

  if (!Array.isArray(ownHosts) || !ownHosts.length) return html;

  try {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    let changed = false;

    Array.from(doc.body.querySelectorAll('a[href]')).forEach((node) => {
      if (!isExternalHref(node.getAttribute('href'), ownHosts)) return;

      const target = node.getAttribute('target');

      // An explicit non-_blank target is an author decision (`_self`, a named
      // frame) — neither target nor rel is touched in that case.
      if (target !== null && target.trim().toLowerCase() !== '_blank') return;

      if (target === null) {
        node.setAttribute('target', '_blank');
        changed = true;
      }

      const rel = node.getAttribute('rel');
      const mergedRel = mergeRel(rel);
      if (mergedRel !== rel) {
        node.setAttribute('rel', mergedRel);
        changed = true;
      }
    });

    if (!changed) return html;

    return doc.body.innerHTML;
  } catch (e) {
    console.warn(`[externalLinks] failed to rewrite external links: ${e && e.message}`);
    return html;
  }
}

/**
 * Hosts that count as our own.
 *
 * Both values, not one or the other: `storeDomain` is the build-time canonical
 * production host, and on staging it still points at production — a CMS link to
 * the host the page is actually served from would then be classified external,
 * making staging behave differently from prod. `window.location.hostname` is
 * also the only source for the brands whose env ships without
 * REACT_APP_STORE_DOMAIN (Enjoyable, DeathRow).
 *
 * @returns {Array<string>}
 */
const getOwnHosts = () => {
  const hosts = [];

  const configured = normalizeHost(storeDomain);
  if (configured) hosts.push(configured);

  const current = typeof window !== 'undefined' ? normalizeHost(window.location && window.location.hostname) : '';
  if (current && !hosts.includes(current)) hosts.push(current);

  return hosts;
};

/**
 * Public entry point: make external links inside a CMS HTML string open in a
 * new tab with `rel="noopener noreferrer"`.
 *
 * In kiosk/POS mode this is a strict no-op — the terminal has no browser chrome
 * to come back from, and an existing manual target="_blank" authored in the CMS
 * is deliberately left in place rather than stripped.
 *
 * @param {string} html raw CMS HTML
 * @returns {string} the input itself when there is nothing to do
 */
export function openExternalLinksInNewTab(html) {
  if (isKiosk) return html;

  return rewriteExternalLinks(html, getOwnHosts());
}

export default openExternalLinksInNewTab;
